/**
 * Licensed Materials - Property of IBM
 *
 * IBM Cognos Products: BI
 *
 * Cloud (C) Copyright IBM Corp. 2016, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../api/VisIndicatorsProviderAPI',
	'./api/ConditionalViewIndicatorAPI',
	'jquery',
	'doT',
	'text!./templates/conditionalViewIndicator.html',
	'../../../../visualizations/renderer/conditions/HistogramView',
	'../../../../widgets/livewidget/nls/StringResources'
], (APIFactory, VisIndicatorsProviderAPI, ConditionalViewIndicatorAPI, $, dot, indicatorTemplate, HistogramView, resources) => {

	const template = dot.template(indicatorTemplate);

	class ConditionalViewIndicator {

		constructor({ content, dashboardAPI, features }) {
			this.content = content;
			this.dashboardApi = dashboardAPI;

			this.viewIsOpen = false;
			this.isVisible = false;
			this.inExpandedMode = false;

			if(!this._isNewConditionalFormatFeature()){
				features.VisIndicators.registerProvider(this.getAPI());
				this.init();
			}
		}

		_isNewConditionalFormatFeature () {
			return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'xtabcondFormat', 'disabled');
		}

		init() {
			this.$el = $(template({
				buttonClass: 'colorPaletteIcon',
				buttonTitle: resources.get('widgetConditionalFormatTitle'),
				ariaLabel: resources.get('widgetConditionalFormatLabel')
			}));
		}

		initEvents() {
			if (!this._eventsInitialized) {
				this.$el.on('mousedown tap keydown', this.onClick.bind(this));

				this.dashboardApi.on('widget:maximize', this.onMaximize, this);
				this.dashboardApi.on('widget:restore', this.onRestore, this);

				const widgetChromeEventRouter = this.getWidgetChromeEventRouter();
				if (widgetChromeEventRouter) {
					widgetChromeEventRouter.trigger('widget:onHideAttachedView');
					widgetChromeEventRouter.on('widgetchrome:selected', this.select, this);
					widgetChromeEventRouter.on('widgetchrome:deselected', this.deselect, this);
					this._eventsInitialized = true;
				}
			}
		}

		removeEvents() {
			this.$el.off();

			this.dashboardApi.off('widget:maximize', this.onMaximize, this);
			this.dashboardApi.off('widget:restore', this.onRestore, this);

			const widgetChromeEventRouter = this.getWidgetChromeEventRouter();
			if (widgetChromeEventRouter) {
				widgetChromeEventRouter.off('widgetchrome:selected', this.select, this);
				widgetChromeEventRouter.off('widgetchrome:deselected', this.deselect, this);
			}
		}

		getAPI() {
			if(this._isNewConditionalFormatFeature()){
				return undefined;
			}else{
				if (!this.api) {
					this.api = APIFactory.createAPI(this, [ConditionalViewIndicatorAPI, VisIndicatorsProviderAPI]);
				}
				return this.api;
			}
		}

		getVisualization() {
			return this.content.getFeature('Visualization');
		}

		getVisApi() {
			const widget = this.content.getFeature('WidgetAPI.deprecated');
			return widget && widget.getVisApi && widget.getVisApi();
		}

		getOwnerWidget() {
			const visApi = this.getVisApi();
			return visApi && visApi.ownerWidget;
		}

		getWidgetChromeEventRouter() {
			const widget = this.getOwnerWidget();
			return widget && widget.widgetChromeEventRouter;
		}

		getIndicatorList() {
			//Assume the widget and vis have all their necessary features loaded by the time indicators are requested
			this.initEvents();

			return [{
				name: 'conditionalViewIcon',
				icon: this.$el
			}];
		}

		destroy() {
			if(!this._isNewConditionalFormatFeature()){
				this.removeEvents();
				this.hideAttachedView();

				this.content = null;
				this.dashboardApi = null;

				if (this.histogramView) {
					this.histogramView.remove();
				}

				this.histogramView = null;
			}
		}

		update(options = {}) {
			this.updatePalette();
			const visApi = this.getVisApi();
			const slotsApi = this.getVisualization().getSlots();
			if (visApi.hasHeatByItem() && slotsApi.isMappingComplete()) {
				this.$el.removeClass('hideIndicator');
				this.isVisible = true;
				if (this.inExpandedMode) {
					this.$el.addClass('disableConditionalIndicator');
				}
			} else {
				this.$el.addClass('hideIndicator');
				this.isVisible = false;
				this.viewIsOpen = false;
				this.hideAttachedView();
			}

			if (options.refresh || options.data) {
				var refreshOpt = options.refresh || {};
				if (options.data) {
					refreshOpt.visModel = this.getVisApi();
					refreshOpt.data = options.data;
					this.data = options.data;
				}
				if (this.histogramView) {
					this.histogramView.refresh(refreshOpt);
				}
			}
		}

		updatePalette() {
			const conditions = this.getVisApi().getConditions();
			const palette = conditions && conditions.palette;
			if (palette && this.palette !== palette) {
				this.palette = palette;

				const colours = palette.getColors().toArray().slice(0, 5);
				const percentHeight = 100 / colours.length;

				const iconBars = colours.map(
					colour => `<div style="height:${percentHeight}%;background-color:${colour.getBackgroundColor()};"></div>`
				);

				this.$el.find('.colorPaletteIcon').html(iconBars.join(''));
			}
		}

		onClick(event) {
			if (this.inExpandedMode) {
				return Promise.resolve();
			}

			if (event.type === 'keydown') {
				// stop propagation on keydown
				event.stopPropagation();

				// if not enter then ignore even all together as well
				var key = event.key || event.keyCode || event.which || '';
				if (key !== 'Enter' && key !== 13) {
					return Promise.resolve();
				}
			}

			if (event.gesture) {
				// prevent the virtual click event from firing. Otherwise this method will be called twice.
				event.gesture.preventDefault();
			}

			if (event.cancelable) {
				event.preventDefault();
			}

			return this.toggleAttachedView();
		}

		close() {
			if (this.isVisible && this.viewIsOpen) {
				return this.toggleAttachedView();
			}
			return Promise.resolve();
		}

		select() {
			if (this.histogramView) {
				this.histogramView.refresh({ select: true });
			}
		}

		deselect() {
			if (this.histogramView) {
				this.histogramView.refresh({ select: false });
			}
		}

		onMaximize({ id }) {
			if (id === this.content.getId()) {
				this.inExpandedMode = true;
				this.$el.addClass('disableConditionalIndicator');
				return this.close();
			}
			return Promise.resolve();
		}

		onRestore({ id }) {
			if (id === this.content.getId()) {
				this.inExpandedMode = false;
				this.$el.removeClass('disableConditionalIndicator');
			}
			return Promise.resolve();
		}

		renderHistogramView() {
			const ownerWidget = this.getOwnerWidget();
			this.histogramView = new HistogramView({
				model: ownerWidget.model,
				visualization: this.getVisualization(),
				visModel: this.getVisApi(),
				data: this.data,
				icons: this.dashboardApi.getFeature('Icons')
			});
			const widgetChromeEventRouter = this.getWidgetChromeEventRouter();
			widgetChromeEventRouter.trigger('widget:onShowAttachedView', this.histogramView);
			return this.histogramView.render();
		}

		toggleAttachedView() {
			if (!this._togglePromise) {
				this._togglePromise = new Promise(resolve => {
					setTimeout(() => resolve(), 0);
				}).then(() => this.viewIsOpen ? this.hideAttachedView() : this.showAttachedView());
				this._togglePromise.catch(() => { }).then(() => this._togglePromise = null);
			}
			return this._togglePromise;
		}

		showAttachedView() {
			this.viewIsOpen = true;
			const widgetChromeEventRouter = this.getWidgetChromeEventRouter();
			return widgetChromeEventRouter ? this.renderHistogramView() : Promise.resolve();
		}

		hideAttachedView() {
			this.viewIsOpen = false;
			const widgetChromeEventRouter = this.getWidgetChromeEventRouter();
			if (widgetChromeEventRouter && this.histogramView) {
				this.histogramView.remove();
				widgetChromeEventRouter.trigger('widget:onHideAttachedView', this.histogramView.$el.clone());
				delete this.histogramView;
			}
			return Promise.resolve();
		}
	}

	return ConditionalViewIndicator;
});
